import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Newsletter = () => {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setErrorMessage(language === 'es' ? 'Por favor ingresa un email válido' : 'Please enter a valid email');
      return;
    }

    setStatus('loading');
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setStatus('success');
        setEmail('');
        
        // Track Google Ads conversion
        if (typeof window.gtag_report_conversion === 'function') {
          window.gtag_report_conversion();
        }
      } else {
        const data = await response.json();
        setStatus('error');
        setErrorMessage(data.detail || (language === 'es' ? 'Error al suscribirse' : 'Subscription error'));
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(language === 'es' ? 'Error de conexión. Intenta de nuevo.' : 'Connection error. Try again.');
    }
  };

  const texts = {
    es: {
      title: 'Mantente Actualizado',
      subtitle: 'Suscríbete a nuestro newsletter y recibe las últimas tendencias tech, guías y recursos directamente en tu inbox.',
      placeholder: 'tu@email.com',
      button: 'Suscribirse',
      loading: 'Suscribiendo...',
      success: '¡Gracias! Te has suscrito exitosamente.',
      privacy: 'Respetamos tu privacidad. Sin spam, solo contenido de valor.'
    },
    en: {
      title: 'Stay Updated',
      subtitle: 'Subscribe to our newsletter and receive the latest tech trends, guides, and resources directly in your inbox.',
      placeholder: 'you@email.com',
      button: 'Subscribe',
      loading: 'Subscribing...',
      success: 'Thanks! You have subscribed successfully.',
      privacy: 'We respect your privacy. No spam, only valuable content.'
    }
  };

  const t = texts[language];

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-[#F7931A]/10 to-transparent" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#F7931A] rounded-full blur-[300px] opacity-10" />
      
      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          {/* Header */}
          <div className="text-center mb-10">
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center justify-center w-16 h-16 glass rounded-2xl mb-6"
            >
              <Mail className="w-8 h-8 text-[#F7931A]" />
            </motion.div>
            <h2 className="font-['Syne'] text-4xl md:text-5xl font-bold text-white mb-4">
              {t.title}
            </h2>
            <p className="text-lg text-[#A1A1AA]">
              {t.subtitle}
            </p>
          </div>

          {/* Form */}
          {status === 'success' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass rounded-2xl p-8 text-center"
            >
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-xl text-white font-medium">{t.success}</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status === 'error') setStatus('idle');
                    }}
                    placeholder={t.placeholder}
                    className="w-full pl-12 pr-4 py-4 glass rounded-xl text-white placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#F7931A]/50 transition-all"
                    data-testid="newsletter-email"
                    disabled={status === 'loading'}
                  />
                </div>
                <motion.button
                  type="submit"
                  whileHover={{ scale: status === 'loading' ? 1 : 1.02 }}
                  whileTap={{ scale: status === 'loading' ? 1 : 0.98 }}
                  disabled={status === 'loading'}
                  className="btn-primary px-8 py-4 flex items-center justify-center gap-2 min-w-[160px] disabled:opacity-70"
                  data-testid="newsletter-submit"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t.loading}
                    </>
                  ) : (
                    t.button
                  )}
                </motion.button>
              </div>

              {/* Error Message */}
              {status === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-red-400 text-sm"
                >
                  <AlertCircle className="w-4 h-4" />
                  {errorMessage}
                </motion.div>
              )}

              {/* Privacy Note */}
              <p className="text-center text-sm text-[#52525B]">
                {t.privacy}
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;
